import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GREETING = {
  role: "assistant",
  content:
    "Ciao! Sono l'assistente di Cosimiroclean. Posso aiutarti a scegliere il pacchetto giusto (Base o Premium) e rispondere a domande su servizi, prezzi e servizio a domicilio. Come posso aiutarti?",
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionId = useRef(
    (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
      `s_${Date.now()}`
  );
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trim();
          if (data === "[DONE]") continue;
          try {
            const obj = JSON.parse(data);
            if (obj.delta) {
              setMessages((m) => {
                const c = [...m];
                c[c.length - 1] = {
                  role: "assistant",
                  content: c[c.length - 1].content + obj.delta,
                };
                return c;
              });
            }
          } catch (e) {
            /* ignore partial */
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { role: "assistant", content: "Spiacente, si è verificato un errore. Riprova." };
        return c;
      });
    } finally {
      setStreaming(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      <button
        data-testid="chat-toggle-button"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full btn-silver flex items-center justify-center shadow-2xl"
        aria-label="Assistente"
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-sm h-[70vh] max-h-[560px] rounded-3xl overflow-hidden flex flex-col glass shadow-2xl border border-[#7EC8E3]/30"
        >
          <div className="px-5 py-4 bg-[#0A1A3A]/90 border-b border-slate-500/20 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#7EC8E3] to-[#A0E0FF] flex items-center justify-center">
              <Sparkles size={18} className="text-[#050B18]" />
            </div>
            <div>
              <div className="font-display font-bold text-slate-100 leading-none">Assistente Cosimiroclean</div>
              <div className="text-[11px] text-[#7EC8E3] mt-0.5">Online · risponde subito</div>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  data-testid={`chat-message-${m.role}`}
                  className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-gradient-to-r from-[#E8E8E8] to-[#C0C0C0] text-[#050B18] font-medium rounded-br-sm"
                      : "bg-[#050B18]/70 border border-slate-500/25 text-slate-200 rounded-bl-sm"
                  }`}
                >
                  {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
          </div>

          <div className="p-3 border-t border-slate-500/20 bg-[#0A1A3A]/80">
            <div className="flex items-end gap-2">
              <textarea
                data-testid="chat-input"
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder="Scrivi un messaggio…"
                className="flex-1 resize-none rounded-xl bg-[#050B18]/70 border border-slate-500/25 px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-[#7EC8E3] max-h-24"
              />
              <button
                data-testid="chat-send-button"
                onClick={send}
                disabled={streaming || !input.trim()}
                className="w-10 h-10 rounded-xl btn-silver flex items-center justify-center shrink-0 disabled:opacity-50"
                aria-label="Invia"
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
